<template>
    <Card pageTitle="Cova Inventory Reconciliation">
        <AllowPermission :permission="PERMISSION_CODES.CMS_COVA_STOCK_IMPORT">
            <div class="cova-reconcile-page">
                <div class="wizard-header">
                    <div>
                        <div class="eyebrow">Daily inventory</div>
                        <h2>Cova inventory reconciliation</h2>
                        <p>Compare the daily Cova inventory export with High Ties stock and apply reviewed quantity adjustments.</p>
                    </div>
                    <div class="header-actions">
                        <router-link to="/cova-import">
                            <Button borderOnly noMargin type="button">Back to Cova Sync</Button>
                        </router-link>
                        <router-link to="/inventory-adjustments/quantity">
                            <Button borderOnly noMargin type="button">Qty Adjustments</Button>
                        </router-link>
                    </div>
                </div>

                <section v-if="operation.visible" ref="operationPanel" class="operation-panel" :class="operation.status">
                    <div class="operation-main">
                        <div class="operation-icon">
                            <i class="material-icons">{{ operationIcon }}</i>
                        </div>
                        <div>
                            <div class="operation-kicker">{{ operationKicker }}</div>
                            <h3>{{ operation.title }}</h3>
                            <p>{{ operation.message }}</p>
                        </div>
                    </div>
                    <div class="operation-progress">
                        <div class="operation-progress__meta">
                            <span>{{ operationDetail }}</span>
                            <strong>{{ numberFormat(operation.progress) }}%</strong>
                        </div>
                        <div class="operation-progress__track">
                            <div class="operation-progress__bar" :style="{ width: `${operation.progress}%` }"></div>
                        </div>
                    </div>
                    <div v-if="applyResult && operation.status === 'success'" class="operation-result">
                        <div>
                            <span class="report-label">Rows applied</span>
                            <strong>{{ numberFormat(applyResult.summary && applyResult.summary.selectedRows) }}</strong>
                        </div>
                        <div>
                            <span class="report-label">Net units</span>
                            <strong :class="deltaClass(applyResult.summary && applyResult.summary.totalNetUnitChange)">
                                {{ signedNumber(applyResult.summary && applyResult.summary.totalNetUnitChange) }}
                            </strong>
                        </div>
                        <div>
                            <span class="report-label">Adjustment</span>
                            <strong>{{ applyResult.adjustment && applyResult.adjustment.name }}</strong>
                        </div>
                    </div>
                </section>

                <div v-if="operationDockVisible" class="operation-dock" :class="[operation.status, operation.type]">
                    <div class="operation-dock__main">
                        <div class="operation-dock__icon">
                            <i class="material-icons">{{ operationIcon }}</i>
                        </div>
                        <div>
                            <div class="operation-dock__title">{{ operation.title }}</div>
                            <div class="operation-dock__message">{{ operation.message }}</div>
                        </div>
                    </div>
                    <div class="operation-dock__progress">
                        <span>{{ operationDetail }}</span>
                        <strong>{{ numberFormat(operation.progress) }}%</strong>
                        <div class="operation-progress__track">
                            <div class="operation-progress__bar" :style="{ width: `${operation.progress}%` }"></div>
                        </div>
                    </div>
                    <button v-if="operation.status !== 'running'" class="operation-dock__dismiss" type="button" @click="dismissOperation">
                        Close
                    </button>
                </div>

                <section class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Analyze inventory file</h3>
                            <p>Select the High Ties location and upload the Cova inventory export.</p>
                        </div>
                        <span class="section-status" :class="{ ready: canAnalyze, working: preflighting }">
                            {{ preflighting ? "Analyzing" : canAnalyze ? "Ready" : "Waiting" }}
                        </span>
                    </div>

                    <div class="form-grid">
                        <SelectDropdown
                            label="Target High Ties location"
                            :options="warehouseOptions"
                            :multiple="false"
                            :value="form.warehouseId"
                            :disabled="inputsLocked"
                            @change="setWarehouse"
                        />
                        <div class="field-block">
                            <label>Reconciliation reference</label>
                            <input
                                v-model="form.importReference"
                                class="text-input"
                                :disabled="inputsLocked"
                                placeholder="COVA-RECONCILE-YYYY-MM-DD"
                            />
                        </div>
                    </div>

                    <div class="mode-options">
                        <label class="check-option">
                            <input v-model="form.mirrorMode" type="checkbox" :disabled="inputsLocked" @change="handleMirrorModeChange" />
                            <span>
                                <strong>Mirror Cova snapshot</strong>
                                <small>Create missing Cova packages and apply Cova as the source of truth.</small>
                            </span>
                        </label>
                        <label class="check-option" :class="{ disabled: !form.mirrorMode }">
                            <input
                                v-model="form.zeroMissingLocalPackages"
                                type="checkbox"
                                :disabled="inputsLocked || !form.mirrorMode"
                                @change="clearAnalysis"
                            />
                            <span>
                                <strong>Zero packages missing from Cova</strong>
                                <small>Set live High Ties package quantities to zero when they are absent from the Cova export.</small>
                            </span>
                        </label>
                    </div>

                    <div
                        class="file-drop"
                        :class="{ disabled: inputsLocked }"
                        role="button"
                        tabindex="0"
                        @click="openStockFilePicker"
                        @keydown.enter.prevent="openStockFilePicker"
                        @keydown.space.prevent="openStockFilePicker"
                    >
                        <span class="file-title">Inventory export</span>
                        <span class="file-name">{{ stockFileName || "Choose Cova inventory file" }}</span>
                        <input
                            ref="stockFileInput"
                            class="file-input"
                            type="file"
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            :disabled="inputsLocked"
                            @change="handleFileSelect"
                        />
                    </div>

                    <div class="action-row">
                        <Button type="button" :disabled="!canAnalyze" @click="runPreflight">
                            {{ preflighting ? "Analyzing..." : form.mirrorMode ? "Analyze Mirror" : "Analyze Inventory" }}
                        </Button>
                        <Button v-if="analysis || applyResult" borderOnly noMargin type="button" :disabled="inputsLocked" @click="reset">
                            Start New Reconciliation
                        </Button>
                    </div>
                </section>

                <section class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Review deltas</h3>
                            <p>{{ form.mirrorMode ? "Selected mirror changes will be applied to match the Cova snapshot." : "Only selected safe matched rows will be applied." }}</p>
                        </div>
                        <span class="section-status" :class="{ ready: analysis && !blockingIssues.length, working: applying }">
                            {{ applying ? "Applying" : analysis ? blockingIssues.length ? "Blocked" : "Reviewed" : "Not checked" }}
                        </span>
                    </div>

                    <UiState v-if="preflighting" type="loading" title="Reading Cova inventory" compact />
                    <UiState
                        v-else-if="!analysis"
                        type="empty"
                        title="No analysis yet"
                        message="Upload an inventory export and run analysis."
                        compact
                    />
                    <div v-else>
                        <div class="metric-grid">
                            <div v-for="metric in summaryMetrics" :key="metric.label" class="metric-cell">
                                <span>{{ metric.label }}</span>
                                <strong>{{ metric.value }}</strong>
                            </div>
                        </div>

                        <div v-if="blockingIssues.length" class="issue-list danger">
                            <h4>Blocking review</h4>
                            <div v-for="issue in blockingIssues" :key="issue.code" class="issue-row">
                                <i class="material-icons">error_outline</i>
                                <span>{{ issue.message }}</span>
                            </div>
                        </div>

                        <div v-if="warningIssues.length" class="issue-list warning">
                            <h4>Warnings</h4>
                            <div v-for="issue in warningIssues" :key="issue.code" class="issue-row">
                                <i class="material-icons">report_problem</i>
                                <span>{{ issue.message }}</span>
                            </div>
                        </div>

                        <div class="table-toolbar">
                            <div class="toolbar-left">
                                <select v-model="rowFilter" class="filter-select">
                                    <option value="all">All rows</option>
                                    <option value="eligible">Safe deltas</option>
                                    <option value="changes">Changes</option>
                                    <option value="blocked">Manual review</option>
                                </select>
                                <span>{{ numberFormat(selectedRowIds.length) }} selected</span>
                            </div>
                            <div class="toolbar-actions">
                                <Button borderOnly noMargin type="button" :disabled="!eligibleRows.length || inputsLocked" @click="selectSafeDeltas">
                                    {{ form.mirrorMode ? "Select Mirror Changes" : "Select Safe Deltas" }}
                                </Button>
                                <Button borderOnly noMargin type="button" :disabled="!selectedRowIds.length || inputsLocked" @click="clearSelection">
                                    Clear
                                </Button>
                            </div>
                        </div>

                        <div class="review-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th class="select-col"></th>
                                        <th>Product</th>
                                        <th>Package</th>
                                        <th>Room</th>
                                        <th>Cova</th>
                                        <th>High Ties</th>
                                        <th>Delta</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in filteredRows" :key="row.rowId" :class="row.status">
                                        <td class="select-col">
                                            <input
                                                type="checkbox"
                                                :checked="isSelected(row.rowId)"
                                                :disabled="!row.applyEligible || inputsLocked"
                                                @change="toggleRow(row.rowId)"
                                            />
                                        </td>
                                        <td>
                                            <strong>{{ row.itemName || row.productName || row.catalogSku }}</strong>
                                            <span>{{ row.catalogSku }}<template v-if="row.supplierSku"> / {{ row.supplierSku }}</template></span>
                                        </td>
                                        <td>{{ row.packageId || "Missing" }}</td>
                                        <td>{{ row.roomName }}</td>
                                        <td>{{ numberFormat(row.covaQty) }}</td>
                                        <td>{{ numberFormat(row.highTiesQty) }}</td>
                                        <td :class="deltaClass(row.delta)">{{ signedNumber(row.delta) }}</td>
                                        <td>
                                            <span class="status-chip" :class="row.severity">{{ statusLabel(row) }}</span>
                                            <small v-if="row.issues && row.issues.length">{{ issueLabel(row) }}</small>
                                        </td>
                                    </tr>
                                    <tr v-if="!filteredRows.length">
                                        <td colspan="8" class="empty-row">No rows match this filter.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="apply-panel" :class="{ applying }">
                            <div class="apply-summary">
                                <span class="report-label">Selected net change</span>
                                <strong :class="deltaClass(selectedNetChange)">{{ signedNumber(selectedNetChange) }}</strong>
                                <small>{{ numberFormat(selectedRowIds.length) }} row{{ selectedRowIds.length === 1 ? "" : "s" }} selected</small>
                            </div>
                            <div class="apply-actions">
                                <Button type="button" :disabled="!canApply" @click="applySelected">
                                    {{ applying ? "Applying adjustments..." : form.mirrorMode ? "Apply Selected Mirror Changes" : "Apply Selected Adjustments" }}
                                </Button>
                                <small v-if="analysis && !canCreateAdjustment" class="permission-note">Qty adjustment permission is required to apply.</small>
                                <div v-if="applying" class="apply-progress-inline">
                                    <div>
                                        <span>{{ operation.message }}</span>
                                        <strong>{{ numberFormat(operation.progress) }}%</strong>
                                    </div>
                                    <div class="operation-progress__track">
                                        <div class="operation-progress__bar" :style="{ width: `${operation.progress}%` }"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section v-if="applyResult" class="section-block">
                    <div class="section-heading">
                        <div>
                            <h3>Applied result</h3>
                            <p>{{ applyResult.importReference }}</p>
                        </div>
                        <span class="section-status ready">Applied</span>
                    </div>
                    <div class="final-report">
                        <div>
                            <span class="report-label">Rows applied</span>
                            <strong>{{ numberFormat(applyResult.summary && applyResult.summary.selectedRows) }}</strong>
                        </div>
                        <div>
                            <span class="report-label">Net units</span>
                            <strong :class="deltaClass(applyResult.summary && applyResult.summary.totalNetUnitChange)">
                                {{ signedNumber(applyResult.summary && applyResult.summary.totalNetUnitChange) }}
                            </strong>
                        </div>
                        <div>
                            <span class="report-label">Adjustment</span>
                            <strong>{{ applyResult.adjustment && applyResult.adjustment.name }}</strong>
                        </div>
                    </div>
                    <div class="report-actions">
                        <router-link v-if="applyResult.adjustment && applyResult.adjustment.name" :to="`/inventory-adjustments/quantity/${applyResult.adjustment.name}`">
                            <Button borderOnly noMargin type="button">Open Adjustment</Button>
                        </router-link>
                        <router-link to="/inventory-manager">
                            <Button borderOnly noMargin type="button">View Inventory</Button>
                        </router-link>
                    </div>
                </section>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapActions, mapState } from "vuex"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Button from "../../components/UI/Button.vue"
import Card from "../../components/UI/Card.vue"
import SelectDropdown from "../../components/crud/SelectDropdown.vue"
import UiState from "../../components/UI/UiState.vue"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import { errMsg } from "../../utils/common"
import { http } from "../../utils/http"
import { consumeCovaSyncHandoff } from "../../utils/cova-sync-session"

export default {
    name: "CovaInventoryReconciliation",
    components: {
        AllowPermission,
        Button,
        Card,
        SelectDropdown,
        UiState,
    },
    data() {
        return {
            PERMISSION_CODES,
            form: {
                warehouseId: "",
                importReference: `COVA-RECONCILE-${new Date().toISOString().slice(0, 10)}`,
                mirrorMode: false,
                zeroMissingLocalPackages: false,
            },
            files: {
                stockFile: null,
            },
            analysis: null,
            applyResult: null,
            preflighting: false,
            applying: false,
            selectedRowIds: [],
            rowFilter: "eligible",
            operation: {
                visible: false,
                type: "",
                status: "idle",
                title: "",
                message: "",
                progress: 0,
                elapsedSeconds: 0,
            },
            operationTimer: null,
        }
    },
    computed: {
        ...mapState("misc", ["warehouses"]),
        ...mapState("auth", ["user"]),
        warehouseOptions() {
            return this.warehouses.map((item) => ({ id: item.id, title: item.name }))
        },
        stockFileName() {
            return this.files.stockFile ? this.files.stockFile.name : ""
        },
        inputsLocked() {
            return this.preflighting || this.applying
        },
        canAnalyze() {
            return !!this.form.warehouseId && !!this.files.stockFile && !this.inputsLocked
        },
        rows() {
            return (this.analysis && this.analysis.rows) || []
        },
        eligibleRows() {
            return this.rows.filter((row) => row.applyEligible)
        },
        blockingIssues() {
            return (this.analysis && this.analysis.blockingErrors) || []
        },
        warningIssues() {
            return (this.analysis && this.analysis.warnings) || []
        },
        filteredRows() {
            if (this.rowFilter === "eligible") return this.rows.filter((row) => row.applyEligible)
            if (this.rowFilter === "changes") return this.rows.filter((row) => row.delta !== 0)
            if (this.rowFilter === "blocked") return this.rows.filter((row) => !row.applyEligible && row.status !== "unchanged")
            return this.rows
        },
        selectedNetChange() {
            return this.rows
                .filter((row) => this.selectedRowIds.includes(row.rowId))
                .reduce((sum, row) => sum + Number(row.delta || 0), 0)
        },
        canCreateAdjustment() {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId: PERMISSION_CODES.CMS_CREATE_ADJUSTMENT,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        canApply() {
            return this.canCreateAdjustment && !!this.selectedRowIds.length && !this.blockingIssues.length && !this.inputsLocked
        },
        operationKicker() {
            if (this.operation.status === "success") return "Completed"
            if (this.operation.status === "error") return "Needs attention"
            return this.operation.type === "apply" ? "Applying adjustments" : "Analyzing inventory"
        },
        operationIcon() {
            if (this.operation.status === "success") return "check_circle"
            if (this.operation.status === "error") return "error_outline"
            return "sync"
        },
        operationDetail() {
            if (this.operation.status === "running") {
                return `${this.operation.elapsedSeconds}s elapsed`
            }
            if (this.operation.status === "success") {
                return "Finished"
            }
            if (this.operation.status === "error") {
                return "Stopped"
            }
            return "Waiting"
        },
        operationDockVisible() {
            return this.operation.visible && (this.operation.status === "running" || this.operation.type === "apply")
        },
        summaryMetrics() {
            const summary = (this.analysis && this.analysis.summary) || {}
            const metrics = [
                { label: "Rows", value: this.numberFormat(summary.totalRows) },
                { label: this.form.mirrorMode ? "Mirror changes" : "Safe deltas", value: this.numberFormat(summary.applyEligibleRows) },
                { label: "Increases", value: this.numberFormat(summary.increaseRows) },
                { label: "Decreases", value: this.numberFormat(summary.decreaseRows) },
                { label: "No change", value: this.numberFormat(summary.unchangedRows) },
                { label: "New packages", value: this.numberFormat(summary.newPackageCandidates) },
                { label: "Net units", value: this.signedNumber(summary.totalNetUnitChange) },
                { label: "Unposted Cova POs", value: this.numberFormat(summary.unpostedCovaPurchaseOrders) },
            ]
            if (this.form.mirrorMode) {
                metrics.splice(6, 0, { label: "Local only", value: this.numberFormat(summary.localOnlyRows) })
            }
            metrics.splice(this.form.mirrorMode ? 7 : 6, 0, { label: "Manual review", value: this.numberFormat(summary.manualReviewRows) })
            return metrics
        },
    },
    mounted() {
        this.setData({ pageTitle: "Cova Inventory Reconciliation" })
        const handoff = consumeCovaSyncHandoff("reconciliation")
        if (handoff) {
            this.form.warehouseId = handoff.warehouseId || ""
            this.files.stockFile = handoff.stockFile || null
        }
    },
    beforeDestroy() {
        this.stopOperationTimer()
    },
    methods: {
        ...mapActions("misc", ["setData"]),
        numberFormat(value) {
            const numberValue = Number(value || 0)
            return Number.isFinite(numberValue) ? numberValue.toLocaleString() : "0"
        },
        signedNumber(value) {
            const numberValue = Number(value || 0)
            if (!Number.isFinite(numberValue)) return "0"
            return `${numberValue > 0 ? "+" : ""}${numberValue.toLocaleString()}`
        },
        deltaClass(value) {
            const numberValue = Number(value || 0)
            if (numberValue > 0) return "positive"
            if (numberValue < 0) return "negative"
            return "neutral"
        },
        statusLabel(row) {
            if (row.applyEligible && row.status === "new_package") return "Create package"
            if (row.applyEligible && row.status === "local_only") return "Zero"
            if (row.applyEligible) return row.delta > 0 ? "Increase" : "Decrease"
            if (row.status === "unchanged") return "No change"
            if (row.status === "local_only") return "Local only"
            if (row.status === "new_package") return "Manual review"
            return "Blocked"
        },
        issueLabel(row) {
            const issue = row.issues && row.issues[0]
            return issue ? issue.message : ""
        },
        setWarehouse(warehouseId) {
            if (this.inputsLocked) return
            this.form.warehouseId = warehouseId || ""
            this.clearAnalysis()
        },
        openStockFilePicker() {
            if (this.inputsLocked || !this.$refs.stockFileInput) return
            this.$refs.stockFileInput.click()
        },
        handleFileSelect(event) {
            if (this.inputsLocked) return
            const file = event.target.files && event.target.files[0]
            this.files.stockFile = file || null
            this.clearAnalysis()
            if (file && event.target) {
                event.target.value = ""
            }
        },
        clearAnalysis() {
            this.analysis = null
            this.applyResult = null
            this.selectedRowIds = []
            this.clearOperation()
        },
        reset() {
            if (this.inputsLocked) return
            if (this.$refs.stockFileInput) this.$refs.stockFileInput.value = ""
            this.files.stockFile = null
            this.form.importReference = `COVA-RECONCILE-${new Date().toISOString().slice(0, 10)}`
            this.form.mirrorMode = false
            this.form.zeroMissingLocalPackages = false
            this.clearAnalysis()
        },
        handleMirrorModeChange() {
            if (this.form.mirrorMode) {
                if (!this.form.importReference || this.form.importReference.startsWith("COVA-RECONCILE-")) {
                    this.form.importReference = `COVA-MIRROR-${new Date().toISOString().slice(0, 10)}`
                }
            } else {
                this.form.zeroMissingLocalPackages = false
                if (!this.form.importReference || this.form.importReference.startsWith("COVA-MIRROR-")) {
                    this.form.importReference = `COVA-RECONCILE-${new Date().toISOString().slice(0, 10)}`
                }
            }
            this.clearAnalysis()
        },
        buildFormData(includeSelection) {
            const formData = new FormData()
            formData.append("stockFile", this.files.stockFile)
            formData.append("warehouseId", this.form.warehouseId)
            formData.append("importReference", this.form.importReference)
            formData.append("mirrorMode", this.form.mirrorMode ? "true" : "false")
            formData.append("zeroMissingLocalPackages", this.form.zeroMissingLocalPackages ? "true" : "false")
            if (includeSelection) {
                formData.append("selectedRowIds", JSON.stringify(this.selectedRowIds))
            }
            return formData
        },
        setAnalysis(result) {
            this.analysis = result
            this.applyResult = null
            this.selectedRowIds = []
        },
        scrollToOperation() {
            this.$nextTick(() => {
                if (this.$refs.operationPanel && typeof this.$refs.operationPanel.scrollIntoView === "function") {
                    this.$refs.operationPanel.scrollIntoView({ behavior: "smooth", block: "start" })
                }
            })
        },
        startOperation(type, title, message) {
            this.stopOperationTimer()
            this.operation = {
                visible: true,
                type,
                status: "running",
                title,
                message,
                progress: type === "apply" ? 12 : 8,
                elapsedSeconds: 0,
            }
            this.operationTimer = window.setInterval(() => {
                const nextProgress = Math.min(92, this.operation.progress + (this.operation.type === "apply" ? 5 : 7))
                const nextElapsed = this.operation.elapsedSeconds + 1
                this.operation = {
                    ...this.operation,
                    progress: nextProgress,
                    elapsedSeconds: nextElapsed,
                    message: this.operationMessageForProgress(this.operation.type, nextProgress),
                }
            }, 1000)
            this.scrollToOperation()
        },
        operationMessageForProgress(type, progress) {
            if (type === "apply") {
                if (progress < 35) return "Uploading the selected reviewed rows."
                if (progress < 65) return "Creating audited quantity adjustments."
                if (progress < 90) return "Updating stock ledger quantities and stock logs."
                return "Finalizing the adjustment result."
            }
            if (progress < 35) return "Uploading the Cova inventory export."
            if (progress < 65) return "Matching packages against High Ties stock."
            if (progress < 90) return "Calculating deltas and manual-review rows."
            return "Preparing the review report."
        },
        finishOperation(title, message) {
            this.stopOperationTimer()
            this.operation = {
                ...this.operation,
                visible: true,
                status: "success",
                title,
                message,
                progress: 100,
            }
            this.scrollToOperation()
        },
        failOperation(title, message) {
            this.stopOperationTimer()
            this.operation = {
                ...this.operation,
                visible: true,
                status: "error",
                title,
                message,
                progress: 100,
            }
            this.scrollToOperation()
        },
        clearOperation() {
            this.stopOperationTimer()
            this.operation = {
                visible: false,
                type: "",
                status: "idle",
                title: "",
                message: "",
                progress: 0,
                elapsedSeconds: 0,
            }
        },
        dismissOperation() {
            if (this.operation.status === "running") return
            this.clearOperation()
        },
        stopOperationTimer() {
            if (this.operationTimer) {
                window.clearInterval(this.operationTimer)
                this.operationTimer = null
            }
        },
        async runPreflight() {
            if (!this.canAnalyze) return
            this.preflighting = true
            this.startOperation(
                "analyze",
                this.form.mirrorMode ? "Analyzing Cova mirror" : "Analyzing Cova inventory",
                "Uploading the Cova inventory export."
            )
            try {
                const res = await http.post("/cova-reconciliation/preflight", this.buildFormData(false))
                this.setAnalysis(res.data.result)
                this.rowFilter = "eligible"
                const summary = res.data.result && res.data.result.summary ? res.data.result.summary : {}
                this.finishOperation(
                    "Analysis complete",
                    `${this.numberFormat(summary.applyEligibleRows)} ${this.form.mirrorMode ? "mirror change" : "safe delta row"}${summary.applyEligibleRows === 1 ? "" : "s"} found. Review the table below before applying changes.`
                )
                this.$toast.success(this.form.mirrorMode ? "Cova mirror analysis completed" : "Cova reconciliation analysis completed")
            } catch (err) {
                const result = err.response && err.response.data && err.response.data.result
                if (result) {
                    this.setAnalysis(result)
                    this.rowFilter = result.blockingErrors && result.blockingErrors.length ? "blocked" : "eligible"
                    this.failOperation("Analysis needs review", "Resolve the blocking items shown below before applying adjustments.")
                    this.$toast.error(this.form.mirrorMode ? "Cova mirror needs review" : "Cova reconciliation needs review")
                } else {
                    this.failOperation("Analysis failed", errMsg(err, "Cova reconciliation analysis failed"))
                    this.$toast.error(errMsg(err, "Cova reconciliation analysis failed"))
                }
            } finally {
                this.preflighting = false
            }
        },
        isSelected(rowId) {
            return this.selectedRowIds.includes(rowId)
        },
        toggleRow(rowId) {
            if (this.inputsLocked) return
            if (this.isSelected(rowId)) {
                this.selectedRowIds = this.selectedRowIds.filter((id) => id !== rowId)
            } else {
                this.selectedRowIds = [...this.selectedRowIds, rowId]
            }
        },
        selectSafeDeltas() {
            if (this.inputsLocked) return
            this.selectedRowIds = this.eligibleRows.map((row) => row.rowId)
            this.rowFilter = "eligible"
        },
        clearSelection() {
            if (this.inputsLocked) return
            this.selectedRowIds = []
        },
        async applySelected() {
            if (!this.canApply) return
            this.applying = true
            this.startOperation(
                "apply",
                this.form.mirrorMode ? "Applying selected mirror changes" : "Applying selected adjustments",
                `Applying ${this.numberFormat(this.selectedRowIds.length)} reviewed row${this.selectedRowIds.length === 1 ? "" : "s"}. Do not refresh this page.`
            )
            try {
                const res = await http.post("/cova-reconciliation/apply", this.buildFormData(true))
                this.applyResult = res.data.result
                const summary = this.applyResult && this.applyResult.summary ? this.applyResult.summary : {}
                this.selectedRowIds = []
                this.finishOperation(
                    "Adjustments applied",
                    `${this.numberFormat(summary.selectedRows)} row${summary.selectedRows === 1 ? "" : "s"} applied with net change ${this.signedNumber(summary.totalNetUnitChange)}.`
                )
                this.$toast.success(this.form.mirrorMode ? "Cova mirror adjustments applied" : "Cova reconciliation adjustments applied")
            } catch (err) {
                const result = err.response && err.response.data && err.response.data.result
                if (result && result.rows) this.analysis = result
                this.failOperation("Apply failed", errMsg(err, "Could not apply Cova reconciliation"))
                this.$toast.error(errMsg(err, "Could not apply Cova reconciliation"))
            } finally {
                this.applying = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.cova-reconcile-page {
    display: grid;
    gap: 20px;
}

.wizard-header {
    display: flex;
    justify-content: space-between;
    gap: 18px;
    align-items: flex-start;
    padding-bottom: 18px;
    border-bottom: 1px solid $lightBorderColor;

    h2 {
        margin: 0;
        color: $primaryColor;
        font-size: 26px;
        font-weight: 700;
    }

    p {
        margin: 8px 0 0;
        max-width: 720px;
        color: rgba($textColor, 0.76);
        line-height: 1.5;
    }
}

.eyebrow {
    color: $secondaryColor;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.header-actions,
.action-row,
.report-actions,
.toolbar-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.section-block {
    border: 1px solid $lightBorderColor;
    border-radius: $cardRadius;
    padding: 18px;
    background: rgba(255, 255, 255, 0.62);
}

.section-heading {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 18px;

    h3 {
        margin: 0;
        color: $textColor;
        font-size: 18px;
        font-weight: 700;
    }

    p {
        margin: 4px 0 0;
        color: rgba($textColor, 0.7);
    }
}

.section-status {
    align-self: flex-start;
    border: 1px solid $lightBorderColor;
    border-radius: 999px;
    padding: 6px 10px;
    color: rgba($textColor, 0.7);
    font-size: 12px;
    font-weight: 700;

    &.ready {
        border-color: rgba($successColor, 0.45);
        color: darken($successColor, 12%);
    }

    &.working {
        border-color: rgba($secondaryColor, 0.5);
        color: darken($secondaryColor, 12%);
    }
}

.operation-panel {
    position: sticky;
    top: 12px;
    z-index: 70;
    display: grid;
    gap: 14px;
    border: 1px solid rgba($primaryColor, 0.18);
    border-radius: 8px;
    padding: 16px;
    background: linear-gradient(180deg, rgba($primaryColor, 0.07), rgba($primaryColor, 0.025));
    box-shadow: 0 10px 24px rgba($primaryColor, 0.08);

    &.running {
        border-color: rgba($secondaryColor, 0.35);
        background: linear-gradient(180deg, rgba($secondaryColor, 0.12), rgba($secondaryColor, 0.035));
    }

    &.success {
        border-color: rgba($successColor, 0.42);
        background: linear-gradient(180deg, rgba($successColor, 0.13), rgba($successColor, 0.035));
    }

    &.error {
        border-color: rgba($dangerColor, 0.38);
        background: linear-gradient(180deg, rgba($dangerColor, 0.12), rgba($dangerColor, 0.035));
    }
}

.operation-main {
    display: grid;
    grid-template-columns: 42px 1fr;
    gap: 12px;
    align-items: flex-start;

    h3 {
        margin: 0;
        color: $primaryColor;
        font-size: 18px;
        font-weight: 700;
    }

    p {
        margin: 4px 0 0;
        color: rgba($textColor, 0.74);
        line-height: 1.45;
    }
}

.operation-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    border-radius: 8px;
    background: white;
    color: $primaryColor;
    box-shadow: 0 4px 12px rgba($primaryColor, 0.12);

    i {
        font-size: 22px;
    }
}

.operation-panel.running .operation-icon i {
    animation: spinOperationIcon 1.1s linear infinite;
}

.operation-kicker {
    color: rgba($textColor, 0.58);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.operation-progress {
    display: grid;
    gap: 7px;
}

.operation-progress__meta {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: rgba($textColor, 0.7);
    font-size: 12px;
    font-weight: 700;
}

.operation-progress__track {
    position: relative;
    overflow: hidden;
    height: 9px;
    border-radius: 999px;
    background: rgba($primaryColor, 0.12);
}

.operation-progress__bar {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, $primaryColor, $secondaryColor);
    transition: width 0.35s ease;
}

.operation-panel.running .operation-progress__bar {
    background-size: 180% 100%;
    animation: sweepOperationBar 1.2s ease-in-out infinite;
}

.operation-result {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;

    > div {
        display: grid;
        gap: 4px;
        border: 1px solid rgba($successColor, 0.24);
        border-radius: 8px;
        padding: 10px 12px;
        background: rgba(255, 255, 255, 0.72);
    }

    strong {
        color: $primaryColor;
        font-size: 18px;
    }
}

.operation-dock {
    position: fixed;
    right: 24px;
    bottom: 24px;
    z-index: 900;
    display: grid;
    grid-template-columns: minmax(260px, 1fr) minmax(180px, 220px) auto;
    gap: 14px;
    align-items: center;
    width: min(720px, calc(100vw - 48px));
    border: 1px solid rgba($primaryColor, 0.16);
    border-radius: 8px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 18px 44px rgba($primaryColor, 0.22);

    &.running {
        border-color: rgba($secondaryColor, 0.5);
    }

    &.success {
        border-color: rgba($successColor, 0.5);
    }

    &.error {
        border-color: rgba($dangerColor, 0.5);
    }
}

.operation-dock__main {
    display: grid;
    grid-template-columns: 34px 1fr;
    gap: 10px;
    min-width: 0;
    text-align: left;
}

.operation-dock__icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: rgba($primaryColor, 0.08);
    color: $primaryColor;

    i {
        font-size: 19px;
    }
}

.operation-dock.running .operation-dock__icon i {
    animation: spinOperationIcon 1.1s linear infinite;
}

.operation-dock__title {
    color: $primaryColor;
    font-weight: 800;
}

.operation-dock__message {
    overflow: hidden;
    color: rgba($textColor, 0.7);
    font-size: 12px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.operation-dock__progress {
    display: grid;
    gap: 6px;
    color: rgba($textColor, 0.66);
    font-size: 12px;
    font-weight: 700;
    text-align: left;

    strong {
        color: $primaryColor;
    }
}

.operation-dock__dismiss {
    border: 1px solid rgba($primaryColor, 0.2);
    border-radius: 6px;
    padding: 8px 10px;
    background: white;
    color: $primaryColor;
    font-weight: 800;
}

.form-grid,
.metric-grid,
.final-report {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}

.mode-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 12px;
    margin-top: 14px;
}

.check-option {
    display: grid;
    grid-template-columns: 18px 1fr;
    gap: 10px;
    align-items: flex-start;
    border: 1px solid rgba($primaryColor, 0.14);
    border-radius: 8px;
    padding: 12px;
    background: rgba($primaryColor, 0.035);
    cursor: pointer;

    input {
        margin-top: 2px;
    }

    strong,
    small {
        display: block;
    }

    strong {
        color: $primaryColor;
        font-size: 13px;
    }

    small {
        margin-top: 3px;
        color: rgba($textColor, 0.68);
        line-height: 1.35;
    }

    &.disabled {
        cursor: not-allowed;
        opacity: 0.62;
    }
}

.field-block {
    display: grid;
    gap: 6px;

    label {
        color: rgba($textColor, 0.72);
        font-size: 12px;
        font-weight: 700;
    }
}

.text-input,
.filter-select {
    width: 100%;
    border: 1px solid $lightBorderColor;
    border-radius: 6px;
    background: white;
    color: $textColor;
    min-height: 42px;
    padding: 8px 10px;
}

.file-drop {
    position: relative;
    display: grid;
    gap: 6px;
    margin: 14px 0 0;
    border: 1px dashed rgba($primaryColor, 0.32);
    border-radius: 8px;
    padding: 16px;
    background: rgba($primaryColor, 0.03);
    cursor: pointer;

    &:focus {
        outline: 2px solid rgba($secondaryColor, 0.48);
        outline-offset: 2px;
    }

    &.disabled {
        cursor: not-allowed;
        opacity: 0.68;
    }
}

.file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    opacity: 0;
    pointer-events: none;
}

.file-title,
.report-label {
    color: rgba($textColor, 0.62);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.file-name {
    color: $textColor;
    font-weight: 700;
    word-break: break-word;
}

.metric-cell,
.final-report > div {
    display: grid;
    gap: 4px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    padding: 12px;
    background: white;

    span {
        color: rgba($textColor, 0.64);
        font-size: 12px;
        font-weight: 700;
    }

    strong {
        color: $primaryColor;
        font-size: 20px;
    }
}

.issue-list {
    display: grid;
    gap: 8px;
    margin-top: 16px;
    border-radius: 8px;
    padding: 12px;

    h4 {
        margin: 0 0 4px;
        font-size: 14px;
        font-weight: 700;
    }

    &.danger {
        background: rgba($dangerColor, 0.1);
        color: darken($dangerColor, 8%);
    }

    &.warning {
        background: rgba($warningColor, 0.14);
        color: darken($warningColor, 22%);
    }
}

.issue-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;

    i {
        font-size: 18px;
    }
}

.table-toolbar,
.apply-panel {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin-top: 18px;
    flex-wrap: wrap;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    color: rgba($textColor, 0.72);
    font-weight: 700;
}

.filter-select {
    width: 180px;
}

.review-table {
    margin-top: 14px;
    overflow-x: auto;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: white;

    table {
        width: 100%;
        min-width: 980px;
        border-collapse: collapse;
    }

    th,
    td {
        border-bottom: 1px solid $lightBorderColor;
        padding: 11px 10px;
        text-align: left;
        vertical-align: top;
    }

    th {
        color: rgba($textColor, 0.66);
        font-size: 12px;
        text-transform: uppercase;
    }

    td strong,
    td span,
    td small {
        display: block;
    }

    td span,
    td small {
        color: rgba($textColor, 0.66);
        font-size: 12px;
    }
}

.select-col {
    width: 42px;
}

.status-chip {
    display: inline-block;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
    background: rgba($primaryColor, 0.1);
    color: $primaryColor;

    &.blocking {
        background: rgba($dangerColor, 0.12);
        color: darken($dangerColor, 8%);
    }

    &.warning {
        background: rgba($warningColor, 0.16);
        color: darken($warningColor, 22%);
    }
}

.positive {
    color: darken($successColor, 12%) !important;
}

.negative {
    color: darken($dangerColor, 6%) !important;
}

.neutral {
    color: rgba($textColor, 0.72) !important;
}

.empty-row {
    text-align: center !important;
    color: rgba($textColor, 0.64);
}

.apply-panel {
    border-top: 1px solid $lightBorderColor;
    padding-top: 14px;

    strong {
        display: block;
        font-size: 22px;
    }

    small {
        display: block;
        color: rgba($textColor, 0.62);
        font-weight: 700;
    }

    &.applying {
        border: 1px solid rgba($secondaryColor, 0.32);
        border-radius: 8px;
        padding: 14px;
        background: rgba($secondaryColor, 0.08);
    }
}

.apply-actions {
    display: grid;
    gap: 10px;
    min-width: 260px;
}

.permission-note {
    color: rgba($dangerColor, 0.78);
    font-weight: 700;
}

.apply-progress-inline {
    display: grid;
    gap: 7px;
    min-width: min(380px, 100%);

    > div:first-child {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        color: rgba($textColor, 0.72);
        font-size: 12px;
        font-weight: 700;
    }
}

@keyframes spinOperationIcon {
    to {
        transform: rotate(360deg);
    }
}

@keyframes sweepOperationBar {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}

@media (max-width: 760px) {
    .wizard-header,
    .section-heading,
    .table-toolbar,
    .apply-panel {
        display: grid;
    }

    .operation-main {
        grid-template-columns: 1fr;
    }

    .operation-dock {
        right: 12px;
        bottom: 12px;
        grid-template-columns: 1fr;
        width: calc(100vw - 24px);
    }

    .operation-dock__message {
        white-space: normal;
    }

    .apply-actions {
        min-width: 100%;
    }
}
</style>
